import { useEffect, useRef } from "react"; // Added useEffect and useRef

import { useQuery, useQueryClient, type Query } from "@tanstack/react-query";

import type { TPartImage, TPartInteractionWithImage } from "~/database/types";
import { useApiClient } from "~/hooks/use-api-client";

import type { SessionCacheData } from "../use-session";

// Define the type for the API response, including the signedUrl
type PartImageWithSignedUrl = TPartImage & { signedUrl: string | null };

// AIDEV-NOTE: Cache-specific extension - images array is added dynamically during cache updates
interface CachedPartInteraction extends TPartInteractionWithImage {
    images?: PartImageWithSignedUrl[];
}

// Define the possible terminal statuses
const TERMINAL_STATUSES: Array<TPartImage["status"]> = ["COMPLETED", "ERROR"];

// Polling interval in milliseconds
const POLLING_INTERVAL_MS = 2000; // Poll every 2 seconds

/**
 * Hook to fetch and poll the status of a single part image.
 *
 * @param partId The ID of the part.
 * @param imageId The ID of the image to monitor. Can be null/undefined if no image is selected yet.
 * @param enabled Whether the query should be enabled (defaults to true if imageId is provided).
 * @param sessionId The session ID for cache updates.
 * @param sessionToken The session token for cache updates.
 * @param partInteractionId The part interaction ID for proper cache key matching.
 * @returns The react-query result for the part image, including status and signed URL.
 */
export function usePartImageStatus(
    partId: string | undefined,
    imageId: string | null | undefined,
    enabled: boolean = !!imageId,
    sessionId?: string,
    sessionToken?: string | null,
    partInteractionId?: string | null,
) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    const queryResult = useQuery<PartImageWithSignedUrl, Error>({
        queryKey: ["partImageStatus", partId, imageId || undefined],
        queryFn: async () => {
            if (!partId || !imageId) {
                throw new Error(
                    "partId and imageId are required to fetch image status",
                );
            }
            // AIDEV-NOTE: partId now sent as query param for GET
            const response = await apiClient.get(
                `/part/image/${imageId}?partId=${partId}`,
            );

            if (!response.ok) {
                let errorData: { error?: string } | undefined;
                try {
                    errorData = (await response.json()) as {
                        error?: string;
                    };
                } catch (e) {
                    // Ignore json parsing error if body is empty or not json
                }
                const errorMessage =
                    errorData?.error ||
                    `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            const data: PartImageWithSignedUrl = await response.json();

            if (!data) {
                throw new Error("No data returned for part image status");
            }
            return data;
        },
        enabled: !!partId && !!imageId && enabled,
        // Refetch configuration for polling
        refetchInterval: (query: Query<PartImageWithSignedUrl, Error>) => {
            const data = query.state.data;
            // Stop polling if the status is terminal or if there's no data
            if (!data || TERMINAL_STATUSES.includes(data.status)) {
                return false;
            }
            // Otherwise, continue polling at the defined interval
            return POLLING_INTERVAL_MS;
        },
        refetchIntervalInBackground: true, // Continue polling even if window is not focused
        refetchOnWindowFocus: false, // Don't refetch just because window regained focus
        staleTime: POLLING_INTERVAL_MS, // Consider data stale slightly faster to ensure polling continues smoothly
    });

    // Store the previous status in a ref to detect transition to terminal
    const previousStatusRef = useRef<TPartImage["status"] | undefined>();

    useEffect(() => {
        const currentData = queryResult.data;
        if (
            partId &&
            imageId &&
            currentData &&
            TERMINAL_STATUSES.includes(currentData.status)
        ) {
            // Check if the status transitioned from non-terminal to terminal
            if (
                previousStatusRef.current &&
                !TERMINAL_STATUSES.includes(previousStatusRef.current)
            ) {
                // AIDEV-NOTE: Build query key with partInteractionId if provided to match the actual cache key
                const partImagesQueryKey = partInteractionId
                    ? ["partImages", partId, partInteractionId]
                    : ["partImages", partId];
                const cachedImageList =
                    queryClient.getQueryData<PartImageWithSignedUrl[]>(
                        partImagesQueryKey,
                    );

                if (cachedImageList) {
                    // Check if the image exists in the list
                    const imageExistsInList = cachedImageList.some(
                        (img) => img.id === imageId,
                    );
                    if (imageExistsInList) {
                        // Update existing image in the list
                        const updatedImageList = cachedImageList.map((img) =>
                            img.id === imageId ? currentData : img,
                        );
                        queryClient.setQueryData(
                            partImagesQueryKey,
                            updatedImageList,
                        );
                    } else {
                        // Add new image to the list if it doesn't exist
                        const updatedImageList = [
                            ...cachedImageList,
                            currentData,
                        ];
                        queryClient.setQueryData(
                            partImagesQueryKey,
                            updatedImageList,
                        );
                    }
                } else {
                    // If no cached list exists, create a new one with this image
                    queryClient.setQueryData(partImagesQueryKey, [currentData]);
                }

                // Update the session cache to include the new image in the part
                if (sessionId) {
                    const sessionQueryKey = [
                        "session",
                        sessionId,
                        sessionToken,
                    ];
                    const cachedSession = queryClient.getQueryData(
                        sessionQueryKey,
                    ) as SessionCacheData;

                    if (
                        cachedSession &&
                        typeof cachedSession === "object" &&
                        "parts" in cachedSession
                    ) {
                        const updatePartWithImage = (
                            part: CachedPartInteraction,
                        ) => {
                            if (part.id === partId) {
                                // If this is the part's default image, update part_image
                                if (part.defaultImageId === imageId) {
                                    return {
                                        ...part,
                                        part_image: currentData,
                                    };
                                }
                                // Also update the images array if it exists
                                const existingImages = part.images || [];
                                const imageExists = existingImages.some(
                                    (img) => img.id === imageId,
                                );

                                if (imageExists) {
                                    return {
                                        ...part,
                                        images: existingImages.map((img) =>
                                            img.id === imageId
                                                ? currentData
                                                : img,
                                        ),
                                    };
                                } else {
                                    return {
                                        ...part,
                                        images: [
                                            ...existingImages,
                                            currentData,
                                        ],
                                    };
                                }
                            }
                            return part;
                        };

                        const updatedSession = {
                            ...cachedSession,
                            parts: {
                                present:
                                    cachedSession.parts.present?.map(
                                        updatePartWithImage,
                                    ) ?? [],
                                other: cachedSession.parts.other,
                            },
                        };
                        queryClient.setQueryData(
                            sessionQueryKey,
                            updatedSession,
                        );
                    }
                }
            }
        }
        // Update the ref with the current status for the next comparison
        if (currentData) {
            previousStatusRef.current = currentData.status;
        }
    }, [
        queryResult.data,
        partId,
        imageId,
        queryClient,
        sessionId,
        sessionToken,
        partInteractionId,
    ]);

    return queryResult;
}
